import type { Text as TextProps } from '@web/graphql-components'
import RichTextEditorComposer from './RichTextEditorComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import TextPlaceholder from './TextPlaceholder'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Box } from '@web/components/atoms/Layout'
import { Button } from '@web/components/atoms/Button'
import HtmlText from './HtmlText'
import { DESKTOP_CONTAINER_QUERY, MOBILE_CONTAINER_QUERY } from '../utils'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { Skeleton } from '@web/components/atoms/Skeleton'

export type EditableTextProps = {
  id: string
  componentId: string
  text: string
  htmlText?: string
  label?: string
  placeholder?: string
  onEdit?: (isEditing: boolean) => void
  onChange: (text: TextProps) => void
}

const Text = ({
  id,
  textValue,
  htmlText,
  mobileStyles,
  styles,
  withPlaceholder,
  isLoading,
  placeholder = 'Enter text here...',
}: TextProps & { withPlaceholder?: boolean; placeholder?: string; isLoading?: boolean }) => {
  const backgroundColor = useColorWithOpacity(styles?.backgroundColor, styles?.backgroundColorOpacity)
  const mobileBackgroundColor = useColorWithOpacity(
    mobileStyles?.backgroundColor,
    mobileStyles?.backgroundColorOpacity,
    backgroundColor,
  )
  const containerRef = useRef<HTMLDivElement | null>(null)
  const textRef = useRef<HTMLDivElement | null>(null)
  const [key, setKey] = useState(0)
  const [hasReadMore, setHasReadMore] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const readMoreAlignment = useMemo(() => {
    if (!hasReadMore || !textValue) {
      return ''
    }

    if (textValue.includes('text-align: center')) {
      return 'center'
    }

    if (textValue.includes('text-align: right')) {
      return 'end'
    }

    return 'start'
  }, [hasReadMore, textValue])

  useEffect(() => {
    if ((!styles?.height && !mobileStyles?.height) || !textRef.current || !containerRef.current) {
      return
    }

    setHasReadMore(textRef.current.getBoundingClientRect().height > containerRef.current.getBoundingClientRect().height)
  }, [mobileStyles?.height, styles?.height])

  useEffect(() => {
    setKey((prevKey) => prevKey + 1)
  }, [textValue])

  return (
    <RichTextEditorComposer key={`${id}-${key}`} initialEditorState={textValue} isEditable={false}>
      <Box
        display="flex"
        flexDirection="column"
        css={{
          backgroundColor,
          borderRadius: styles?.borderRadius,
          [MOBILE_CONTAINER_QUERY]: {
            p: mobileStyles?.padding ?? 0,
            backgroundColor: mobileBackgroundColor,
          },
          [DESKTOP_CONTAINER_QUERY]: {
            p: styles?.padding ?? 0,
            backgroundColor,
          },
        }}
        className="c-component-wrapper"
        width="100%"
      >
        {isLoading ? (
          <Skeleton w="20ch" h="4ch" />
        ) : (
          <Box
            ref={containerRef}
            overflow="hidden"
            css={{
              [MOBILE_CONTAINER_QUERY]: {
                height: isExpanded ? 'auto' : (mobileStyles?.height ?? 'auto'),
              },
              [DESKTOP_CONTAINER_QUERY]: {
                height: isExpanded ? 'auto' : (styles?.height ?? 'auto'),
              },
            }}
          >
            {htmlText ? (
              <Box ref={textRef}>
                <HtmlText html={htmlText} />
              </Box>
            ) : (
              <RichTextPlugin
                contentEditable={
                  <Box ref={textRef}>
                    <ContentEditable className="c-editor-input" />
                  </Box>
                }
                // eslint-disable-next-line react/jsx-no-useless-fragment
                placeholder={withPlaceholder ? <TextPlaceholder placeholder={placeholder} /> : <></>}
                ErrorBoundary={LexicalErrorBoundary}
              />
            )}
          </Box>
        )}
        {hasReadMore && (
          <Box
            css={{
              [MOBILE_CONTAINER_QUERY]: {
                alignSelf: readMoreAlignment ?? mobileStyles?.alignSelf ?? 'center',
              },
              [DESKTOP_CONTAINER_QUERY]: {
                alignSelf: readMoreAlignment ?? styles?.alignSelf ?? 'center',
              },
            }}
          >
            <Button
              id="read-more"
              buttonAppearance="unstyled"
              variant="plain"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Read less...' : 'Read more...'}
            </Button>
          </Box>
        )}
      </Box>
    </RichTextEditorComposer>
  )
}

export default Text
